/**
 * One search result → one collection-kit item (MK-4a).
 *
 * This is where MK-2b's classifier finally has a consumer. Pure apart from
 * reading `document` for nothing — no DOM is built here; the kit's item view
 * owns that.
 *
 * THE CARD ANATOMY, and why each field is what it is:
 *
 *  · `title` is the FILENAME. The donor labelled every card with
 *    `extractedFrom` — `template-ecogardensolution.c…` — which is identical on
 *    four consecutive cards and therefore discriminates between none of them.
 *  · `meta` is `1600×900 · WEBP`, the facts that differ card to card.
 *    Provenance moves to the detail view, where there is room to say it
 *    properly.
 *  · `poster.fit`/`background` come from `classifyMedia`, not from the grid.
 *    At list time the index gives a width and no height, so most alpha-capable
 *    assets classify `unknown` → `contain`, which is the deliberate asymmetric
 *    bias: a letterboxed photo is a blemish, a cropped logo is a bug.
 */

import { classifyMedia, normalizeMediaFormat } from '../../shared/media-kind.js';

/** `1600×900 · WEBP`, dropping whichever half is unknown. */
export function metaLineFor(result) {
  const raw = result?._raw || {};
  const variant = pickWidestVariant(result);
  const width = Number(variant?.width || raw.width || 0);
  const height = Number(variant?.height || raw.height || 0);
  const format = normalizeMediaFormat({
    format: raw.format || variant?.format,
    filename: raw.filename || variant?.filename,
    url: result?.thumb
  });
  const parts = [];
  if (width && height) parts.push(`${width}×${height}`);
  else if (width) parts.push(`${width}w`);
  if (format) parts.push(format.toUpperCase());
  return parts.join(' · ');
}

function pickWidestVariant(result) {
  const variants = result?.variants || result?._raw?._variants || [];
  let best = null;
  for (const variant of variants) {
    if (!best || Number(variant?.width || 0) > Number(best?.width || 0)) best = variant;
  }
  return best;
}

/**
 * @param {object} result — an adapter search result
 * @param {{sourceKey: string, currentUrl?: string, sourceName?: string}} ctx
 * @returns {object} a collection-kit item
 */
export function toCollectionItem(result, ctx = {}) {
  const raw = result?._raw || {};
  const variant = pickWidestVariant(result);
  const kind = classifyMedia({
    format: raw.format || variant?.format,
    filename: raw.filename || variant?.filename || result?.title,
    url: result?.thumb,
    naturalWidth: variant?.width || raw.width,
    naturalHeight: variant?.height || raw.height
  });

  const isCurrent =
    !!ctx.currentUrl &&
    !!result?._sourceUrl &&
    String(result._sourceUrl) === String(ctx.currentUrl);
  const dead = !!result?.dead;

  return {
    id: String(result?.id ?? ''),
    // MK-6: the kit's own hover lift, opted into rather than reimplemented.
    // `.pb-coll-card-lift` is the template picker's class and its transition,
    // its shadow and its reduced-motion behaviour all come from collection.css.
    className: 'pb-coll-card-lift',
    // Filename first; a stock result has no filename, and its title IS its
    // description, which is the discriminating string there.
    title: String(raw.filename || result?.title || 'Image'),
    titleAttr: String(result?.title || raw.filename || ''),
    // A dead record's dimensions are not the useful fact about it. The reason
    // goes in the meta line rather than a CSS ::after: generated content is
    // invisible to jsdom, unreliably announced by screen readers, and cannot
    // be asserted — which is three ways of saying it is not really there.
    meta: dead ? 'Source offline' : metaLineFor(result),
    // The kit filters on `searchText ?? [title, meta]`. The adapter has
    // already matched, but a chip change re-filters in place, and the
    // provenance/author fields must stay reachable then.
    searchText: searchTextFor(result),
    poster: {
      kind: 'image',
      src: result?.thumb || '',
      alt: String(result?.title || ''),
      // 3/2 — the token the picker's thumbnails already used. See plan §0.3:
      // taking the existing ratio rather than minting a fourth one is what
      // keeps the skeleton, the card and the geometry baseline in agreement.
      aspect: 'wide',
      fit: kind.fit,
      background: kind.checker ? 'checker' : 'surface'
    },
    state: {
      // MK-3: a known-dead source is not a control. The kit paints
      // `aria-disabled` and the surface refuses activation.
      disabled: dead,
      current: isCurrent
    },
    // Everything the detail view and the pick path need, carried through
    // rather than looked up again — the kit hands the item back verbatim.
    data: {
      result,
      sourceKey: ctx.sourceKey,
      sourceName: ctx.sourceName || '',
      dead,
      isCurrent,
      classification: kind
    }
  };
}

/** The searchable text for one result — filename, title, author, provenance. */
export function searchTextFor(result) {
  const raw = result?._raw || {};
  return [result?.title, result?.author, raw.filename, raw.extractedFrom, raw.sectionCategory]
    .filter(Boolean)
    .join(' ');
}
